<template>
  <div>
    <section class="content-group -bg-01 -center -no-spacer -vs-medium -column">
      <div class="group -flex -between -narrow -vs-small -vs-bottom -vs-collapse-bottom">
        <h1 class="heading -primary">Calorie Calculator</h1>
        <div class="spa-heading-action">
          <router-link to="/my/settings/energy/calorie">Back</router-link>
        </div>
      </div>
      <div class="group -narrow -vs-small -vs-collapse-top">
        <h2 class="heading -tertiary">Calculate your daily values:</h2>
      </div>

      <!-- Calorie Calculator -->
      <div
        class="calculator-card group -flex -between -vs-center -narrow -vs-small -hs-medium -bg-08 -only-rounder -stack-space-double -energy"
      >
        <div class="calculator">
          <vee-form v-slot="{ validate }" as="">
            <form
              ref="formRef"
              class="calorie-calculator"
              method="post"
              novalidate
              @submit.prevent="calculateCalories(validate)"
            >
              <select-input
                v-model="measurementSystem"
                input-id="measurementSystem"
                label="Units"
                rules="required"
                :options="measurementSystemsOptions"
              />

              <text-input
                v-model="energy.birthYear"
                input-id="birthYear"
                type="number"
                label="Birth Year"
                placeholder="####"
                :rules="`required|numeric|min_value:1900|max_value:${localYear}`"
                validate-as="Birth year"
                step="1"
                min="1900"
                :max="localYear"
              />

              <!-- imperial vs metric toggles -->
              <div v-if="measurementSystem === 'imperial'">
                <div class="-flex -between">
                  <div class="height">
                    <text-input
                      v-model.number="conversionData.usHeightFeet"
                      input-id="usHeightFeet"
                      type="number"
                      label="Height (ft)"
                      placeholder="##"
                      rules="required|numeric|min_value:1|max_value:10"
                      step="1"
                      min="1"
                      max="10"
                    />
                  </div>
                  <div class="height">
                    <text-input
                      v-model.number="conversionData.usHeightInch"
                      input-id="usHeightInch"
                      type="number"
                      label="Height (in)"
                      placeholder="##"
                      rules="required|numeric|min_value:0|max_value:11"
                      step="1"
                      min="1"
                      max="11"
                    />
                  </div>
                </div>

                <text-input
                  v-model.number="conversionData.usWeightLbs"
                  input-id="usWeightLbs"
                  type="number"
                  label="Weight (lbs)"
                  placeholder="###"
                  rules="required|numeric|min_value:1|max_value:1000"
                  step="1"
                  min="1"
                  max="1000"
                />
              </div>
              <div v-else>
                <text-input
                  v-model.number="energy.currentHeight"
                  input-id="currentHeight"
                  type="number"
                  label="Height (cm)"
                  placeholder="###"
                  rules="required|numeric|min_value:30|max_value:304"
                  step="1"
                  min="30"
                  max="304"
                />

                <text-input
                  v-model.number="energy.currentWeight"
                  input-id="currentWeight"
                  type="number"
                  label="Weight (kg)"
                  placeholder="###"
                  rules="required|numeric|min_value:1|max_value:500"
                  step="1"
                  min="1"
                  max="500"
                />
              </div>
              <!-- end toggles -->

              <select-input
                v-model.number="energy.activityLevel"
                input-id="activityLevel"
                label="Activity Level"
                rules="required"
                :options="activityLevelsOptions"
                validate-as="Activity level"
              />

              <submit-button
                :submitting="calculating"
                label="Calculate"
                submitting-label="Processing…"
                classes="-main calculate"
                @submit-action="calculateCalories(validate)"
              />
            </form>
          </vee-form>
        </div>
        <div ref="estimate" class="calorie-estimate">
          <div class="heading -tertiary">
            Estimated Daily<br />
            Energy Needs
          </div>
          <div class="estimate-meter" role="status" aria-live="polite">
            <div class="plus-minus">+/-</div>
            <div v-if="calculatedOnce && energy.dailyCalorieType" class="count">
              {{ calorieTypes[energy.dailyCalorieType].calMin }} -
              {{ calorieTypes[energy.dailyCalorieType].calMax }}
            </div>
            <div v-else class="count">--</div>
            <div class="cal">cal</div>
          </div>
        </div>
      </div>
      <!-- End Calorie Calculator -->

      <div class="group -narrow -vs-small -vs-collapse-top">
        <checkbox-input v-model="consent" input-id="consent" :hi-vis="true">
          <template #label>
            <span>
              Save all personal data to allow for easy editing when returning to the calculator.
              <strong>If unchecked, only estimated daily energy needs will be saved.</strong>
            </span>
          </template>
        </checkbox-input>
      </div>

      <!-- Page Action -->
      <div class="spa-action">
        <submit-button
          v-if="calculatedOnce && energy.dailyCalories"
          :submitting="saving"
          label="Save"
          submitting-label="Processing…"
          classes="-main"
          @submit-action="saveSettings"
        />
        <button v-else class="btn -main -inactive" disabled="disabled">
          Calculate before saving
        </button>
      </div>
      <!-- End Page Action -->
    </section>

    <calorie-more-info :show-calories="true" title="How are the ranges organized?">
      <p>
        Your calculated range is based on the Harris-Benedict equation plus your selected activity
        level, and is presented as a high-low estimate of calories needed per day. To help
        understand each range, they are broken down into common characteristics below:
      </p>
    </calorie-more-info>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { Form as VeeForm } from 'vee-validate'
import { scrollToElemTop } from '@/utils/scroll-to'
import SubmitButton from '@/components/buttons/submit-button.vue'
import TextInput from '@/components/forms/text-input.vue'
import SelectInput from '@/components/forms/select-input.vue'
import CheckboxInput from '@/components/forms/checkbox-input.vue'
import CalorieMoreInfo from '@/components/users/settings/energy/calorie-more-info.vue'

/**
 * Step 3c - Calorie Calculator
 *
 * A basic form that uses the harris benedict formula (1990 revision)
 * plus a multiplier for activity to determine the users calorieType range.
 */
export default defineComponent({
  name: 'CalorieCalculatorStep',
  components: {
    SubmitButton,
    CalorieMoreInfo,
    VeeForm,
    TextInput,
    SelectInput,
    CheckboxInput,
  },
  props: {
    calorieTypes: {
      type: Object,
      required: true,
    },
    activityLevels: {
      type: Object,
      required: true,
    },
    measurementSystems: {
      type: Object,
      required: true,
    },
    initialMeasurementSystem: {
      type: String,
      required: true,
      validator: (val) => ['imperial', 'metric'].includes(val),
    },
    initialEnergy: {
      type: Object,
      required: true,
    },

    initialConsent: {
      type: Boolean,
      default: false,
    },

    // loading state from parent
    saving: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['save-action'],
  data() {
    // initial form data, set defaults
    const energy = {
      // defaults
      activityLevel: '',

      // overrides
      ...this.initialEnergy,
    }

    // convert 0 to empty string if data is set
    energy.activityLevel = energy.activityLevel || ''
    energy.currentHeight = Number.parseFloat(energy.currentHeight)
    energy.currentWeight = Number.parseFloat(energy.currentWeight)

    return {
      // initialize from prop, but then self manage
      measurementSystem: this.initialMeasurementSystem,
      consent: !!this.initialConsent,
      energy,

      // form data that must be converted before saving in energy
      conversionData: {
        usHeightFeet: '',
        usHeightInch: '',
        usWeightLbs: '',
      },

      // used for calculating form
      calculating: false,
      calculatedOnce: false,
    }
  },

  computed: {
    localYear() {
      return new Date().getFullYear()
    },

    measurementSystemsOptions() {
      return Object.keys(this.measurementSystems).map((value) => ({
        value,
        label: this.measurementSystems[value],
      }))
    },

    activityLevelsOptions() {
      return [{ value: '', label: 'Select One' }].concat(
        Object.keys(this.activityLevels).map((value) => ({
          value,
          label: this.activityLevels[value].name,
        })),
      )
    },
  },

  /**
   * Set imperial values on initial load
   */
  created() {
    this.conversionData = this.convertToImperial()
  },

  methods: {
    /**
     * Convert height and width from metric to imperial
     * @see https://www.google.com/search?q=inch+in+cm
     * @see https://www.google.com/search?q=lb+in+kg
     */
    convertToImperial() {
      const inchInCm = 0.393701
      const kgInLb = 2.20462

      // metric values stored in db
      const heightCm = this.energy.currentHeight || 0.0
      const weightKg = this.energy.currentWeight || 0.0

      // convert cm height to ft and inch
      let usHeightInch = heightCm * inchInCm
      const usHeightFeet = Math.floor(usHeightInch / 12)
      usHeightInch = Math.round(usHeightInch - usHeightFeet * 12)

      // convert kg weight to lb
      const usWeightLbs = Math.round(weightKg * kgInLb)

      return {
        usHeightFeet,
        usHeightInch,
        usWeightLbs,
      }
    },

    /**
     * Convert height and weight from imperial to metric
     * @see https://www.google.com/search?q=inch+in+cm
     * @see https://www.google.com/search?q=lb+in+kg
     */
    convertToMetric() {
      const cmInInch = 2.54
      const lbInKg = 0.453592

      // convert ft / in to metric cm
      const totalInches = this.conversionData.usHeightFeet * 12 + this.conversionData.usHeightInch
      const currentHeight = (totalInches * cmInInch).toFixed(2)

      // convert lb to metric kg
      const currentWeight = (this.conversionData.usWeightLbs * lbInKg).toFixed(2)

      return {
        currentHeight,
        currentWeight,
      }
    },

    /**
     * Use the harris benedict formula (1990 revision) plus a multiplier for
     * activity to determine the users calorieType range.
     *
     * User will always be fit-into one of the calorieType ranges and that
     * value is ultimately what will be saved to the db.
     *
     * @see https://en.wikipedia.org/wiki/Harris%E2%80%93Benedict_equation
     */
    async calculateCalories(validate) {
      if (this.calculating) {
        return
      }

      try {
        this.calculating = true

        const results = await validate()

        if (!results.valid) {
          throw Error('Invalid Form')
        }

        // convert height and weight to metric and prep for saving
        if (this.measurementSystem === 'imperial') {
          const { currentHeight, currentWeight } = this.convertToMetric()
          this.energy.currentHeight = currentHeight
          this.energy.currentWeight = currentWeight
        }

        /*
        harris-benedict formula from 1990

        male: 	 BMR = (10 × weight in kg) + (6.25 × height in cm) - (5 × age in years) + 5
        female: BMR = (10 × weight in kg) + (6.25 × height in cm) - (5 × age in years) - 161

        But since we do not show sex, we split the difference
        split: BMR = (10 × weight in kg) + (6.25 × height in cm) - (5 × age in years) + ((-161 + 5) / 2)
         */
        const sexMetric = -78

        const currentAge = Number.parseInt(this.localYear) - Number.parseInt(this.energy.birthYear)

        const baseBMR =
          10 * this.energy.currentWeight +
          6.25 * this.energy.currentHeight -
          5 * currentAge +
          sexMetric

        const { multiplier } = this.activityLevels[this.energy.activityLevel]
        const dailyCalories = Math.round(baseBMR * multiplier)

        // console.log('baseBMR', sexMetric, baseBMR, dailyCalories)

        // find the calorieType entry that fits the BMR
        let dailyCalorieType = Object.keys(this.calorieTypes).reduce((acc, key) => {
          const calorieType = this.calorieTypes[key]

          if (dailyCalories >= calorieType.calMin && dailyCalories <= calorieType.calMax) {
            return key
          }

          return acc
        }, 0)

        // outside of range, so first either first or last entry
        if (!dailyCalorieType) {
          const minKey = 1
          const maxKey = Object.keys(this.calorieTypes).reduce((acc, key) => {
            const intKey = Number.parseInt(key)
            if (intKey >= acc) {
              return intKey
            }

            return acc
          }, minKey)

          if (dailyCalories < this.calorieTypes[minKey].calMin) {
            dailyCalorieType = minKey
          } else if (dailyCalories > this.calorieTypes[maxKey].calMax) {
            dailyCalorieType = maxKey
          }
        }

        // update form state in prep for saving
        this.energy.dailyCalorieType = dailyCalorieType
        this.energy.dailyCalories = this.calorieTypes[dailyCalorieType].calAvg
        this.calculatedOnce = true

        // scroll to calculator if mobile
        if (window && window.innerWidth <= 600) {
          scrollToElemTop({
            elem: this.$refs.estimate,
            ms: 250,
          })
        }
      } catch (e) {
        // form validation or missing daily daily calorie type
        console.error(e)
      } finally {
        this.calculating = false
      }
    },

    // saves snake_case to make backend easier
    saveSettings() {
      // separate out data that requires consent and do not even
      // send it to server at all
      const consentData = this.consent
        ? {
            birth_year: this.energy.birthYear,
            current_height: this.energy.currentHeight,
            current_weight: this.energy.currentWeight,
            activity_level: this.energy.activityLevel,
          }
        : {}

      const formData = {
        energy_method: this.$route.params.energyMethod,
        consent: this.consent,
        daily_calorie_type: this.energy.dailyCalorieType,
        ...consentData,
      }

      const userData = {
        measurement_system: this.measurementSystem,
      }

      // return to step 1
      const nextRoute = {
        path: '/my/settings/energy',
        query: {
          success: 'calorie',
        },
      }

      this.$emit('save-action', { formData, userData, nextRoute })
    },
  },
})
</script>
